'use client';

import { useCallback } from 'react';
import dynamic from 'next/dynamic';
import { useAuthBoot } from '../../context/AuthBootContext';
import { cn } from '@/lib/utils/cn';
import { DEFAULT_TEXT_COLOR } from '../../constants';

const ConsentCheckbox = dynamic(
  () => import('./ConsentCheckbox').then((module) => module.ConsentCheckbox),
  {
    ssr: false,
    loading: () => <span className="mt-1 h-4 w-4 shrink-0" aria-hidden="true" />,
  }
);

interface IAdvancedTermsProps {
  textColor?: string;
  hasConsent?: boolean;
  onConsentChange?: (isConsented: boolean) => void;
  isConsentRequired?: boolean;
  sanitizedHtml: string;
  isPlateStyle?: boolean;
}

export function AdvancedTerms({
  textColor,
  hasConsent = false,
  onConsentChange,
  isConsentRequired = false,
  sanitizedHtml,
  isPlateStyle = false,
}: IAdvancedTermsProps): React.ReactElement {
  const { authBoot } = useAuthBoot();

  const resolvedTextColor = textColor || authBoot.storeTheme.login?.textColor || DEFAULT_TEXT_COLOR;

  const handleCheckboxChange = useCallback(
    (checked: boolean): void => {
      onConsentChange?.(checked);
    },
    [onConsentChange]
  );

  return (
    <div
      className={cn(
        'consent-section flex flex-row items-start gap-2 min-w-0',
        isConsentRequired ? 'text-left' : ''
      )}
      style={{ color: resolvedTextColor }}
    >
      {isConsentRequired && (
        <ConsentCheckbox
          checked={hasConsent}
          onCheckedChange={handleCheckboxChange}
          isPlateStyle={isPlateStyle}
        />
      )}
      <div
        className={cn(
          'min-w-0 flex-1 break-words',
          '[&_a]:text-inherit [&_a]:underline',
          '[&_.ql-size-small]:text-sm',
          '[&_.ql-size-large]:text-lg',
          '[&_.ql-size-huge]:text-xl'
        )}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    </div>
  );
}
